/** EmailHealthcheckTab — Diagnóstico bajo demanda de correos en 'Procesados'
 *  que no generaron ticket. Solo ADMIN. READ-ONLY sobre el buzón. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Progress } from '../../../components/ui/progress';
import { Badge } from '../../../components/ui/badge';
import { Loader2, Download, Play, MailSearch, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../context/AuthContext';

const CAT_STYLE = {
  PERDIDA: 'bg-red-100 text-red-700 border-red-200',
  ANEXADO_RIESGO: 'bg-amber-100 text-amber-700 border-amber-200',
  REVISAR: 'bg-orange-100 text-orange-700 border-orange-200',
  ANEXADO: 'bg-slate-100 text-slate-600 border-slate-200',
  ESPERADO: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

export default function EmailHealthcheckTab() {
  const { api } = useAuth();
  const [desde, setDesde] = useState('2026-08-01');
  const [hasta, setHasta] = useState('');
  const [correoFiltro, setCorreoFiltro] = useState('');
  const [running, setRunning] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [estado, setEstado] = useState(null);
  const [filas, setFilas] = useState(null);
  const [ultima, setUltima] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [jobId, setJobId] = useState(null);
  const pollRef = useRef(null);

  const cargarUltima = useCallback(async () => {
    try {
      const { data } = await api.get('/tickets/email-healthcheck/last');
      setUltima(data);
    } catch { /* noop */ }
  }, [api]);

  useEffect(() => {
    cargarUltima();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [cargarUltima]);

  const finalizar = useCallback(async (id) => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    try {
      const { data } = await api.get(`/tickets/email-healthcheck/result/${id}`);
      setFilas((data.resultado && data.resultado.filas) || []);
    } catch (e) {
      toast.error('No se pudo obtener el resultado del diagnóstico');
    }
    setRunning(false);
    setProgreso(100);
    cargarUltima();
  }, [api, cargarUltima]);

  const ejecutar = async () => {
    setRunning(true); setFilas(null); setProgreso(0); setEstado('EN_PROCESO');
    try {
      const { data } = await api.post('/tickets/email-healthcheck/run', null, { params: { desde, hasta: hasta || undefined } });
      const id = data.job_id;
      setJobId(id);
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/tickets/email-healthcheck/status/${id}`);
          setEstado(st.estado);
          setProgreso(st.porcentaje || 0);
          if (st.estado === 'COMPLETADO') finalizar(id);
          else if (st.estado === 'ERROR') {
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
            setRunning(false);
            toast.error(`El diagnóstico falló: ${st.error || 'error desconocido'}`);
          }
        } catch { /* seguir sondeando */ }
      }, 1500);
    } catch (e) {
      setRunning(false);
      toast.error(e?.response?.data?.detail || 'No se pudo iniciar el diagnóstico');
    }
  };

  const descargar = async () => {
    if (!jobId) return;
    setExporting(true);
    try {
      const resp = await api.get(`/tickets/email-healthcheck/export/${jobId}`, {
        params: { correo: correoFiltro || undefined }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `email_healthcheck_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo descargar el Excel');
    } finally {
      setExporting(false);
    }
  };

  const filasFiltradas = (filas || []).filter(
    (f) => !correoFiltro || (f.correo || '').toLowerCase().includes(correoFiltro.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="email-healthcheck-tab">
      <Card>
        <CardContent className="py-5 space-y-4">
          <div className="flex items-start gap-2 text-slate-600">
            <MailSearch className="w-5 h-5 mt-0.5 text-[#4E6151]" />
            <p className="text-sm">
              Analiza la carpeta <b>Procesados</b> del buzón (solo lectura) e identifica los correos que
              <b> no generaron un ticket</b>, con el motivo. No modifica ni mueve correos.
            </p>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">Desde</Label>
              <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
                     className="h-9 w-40" data-testid="email-hc-desde" disabled={running} />
            </div>
            <div>
              <Label className="text-xs">Hasta (opcional)</Label>
              <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
                     className="h-9 w-40" data-testid="email-hc-hasta" disabled={running} />
            </div>
            <Button onClick={ejecutar} disabled={running} data-testid="email-hc-run-btn"
                    className="bg-[#4E6151] hover:bg-[#3f4f42]">
              {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              {running ? 'Ejecutando…' : 'Ejecutar diagnóstico'}
            </Button>
            {ultima?.updated_at && (
              <span className="text-xs text-slate-500 flex items-center gap-1 ml-auto" data-testid="email-hc-last-run">
                <Clock className="w-3.5 h-3.5" />
                Última ejecución: {new Date(ultima.updated_at).toLocaleString('es-PE', { timeZone: 'America/Lima' })}
                {typeof ultima.total === 'number' ? ` · ${ultima.total} correos analizados` : ''}
              </span>
            )}
          </div>

          {running && (
            <div data-testid="email-hc-progress-wrap">
              <Progress value={progreso} className="h-2" data-testid="email-hc-progress" />
              <p className="text-xs text-slate-500 mt-1">{estado === 'EN_PROCESO' ? `Analizando… ${progreso}%` : `${progreso}%`}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {filas && (
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <div className="flex-1 min-w-[220px]">
                <Input placeholder="Filtrar por dirección de correo…" value={correoFiltro}
                       onChange={(e) => setCorreoFiltro(e.target.value)} className="h-9"
                       data-testid="email-hc-filtro-correo" />
              </div>
              <span className="text-sm text-slate-500" data-testid="email-hc-count">
                {filasFiltradas.length} correo(s)
              </span>
              <Button variant="outline" onClick={descargar} disabled={exporting || filasFiltradas.length === 0}
                      data-testid="email-hc-export-btn">
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Descargar Excel
              </Button>
            </div>

            <div className="overflow-auto border border-slate-200 rounded-md">
              <table className="w-full text-sm" data-testid="email-hc-table">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium whitespace-nowrap">Fecha (Lima)</th>
                    <th className="text-left px-3 py-2 font-medium">Dirección de correo</th>
                    <th className="text-left px-3 py-2 font-medium">Asunto</th>
                    <th className="text-left px-3 py-2 font-medium">Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {filasFiltradas.length === 0 ? (
                    <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">Sin resultados</td></tr>
                  ) : filasFiltradas.map((f, i) => (
                    <tr key={i} className="border-t border-slate-100 align-top">
                      <td className="px-3 py-2 whitespace-nowrap text-slate-600">{f.fecha}</td>
                      <td className="px-3 py-2 text-slate-700">{f.correo}</td>
                      <td className="px-3 py-2 text-slate-700">{f.asunto}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={CAT_STYLE[f.categoria] || CAT_STYLE.ANEXADO}>
                          {f.motivo}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
